import type { DealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import { getColumnTitle, getColumnTooltip, type FieldDefinition } from '@src/utils/fieldDefinitionUtils';
import type { ColumnDef, useDisclosureWithContext } from '@talos/kyoko';
import { Button, ButtonVariants, FormControlSizes, Icon, IconName, Text, Tooltip } from '@talos/kyoko';
import type { ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { get } from 'lodash-es';
import { useMemo } from 'react';

export type DealProductBuilderColumn = ColumnDef<DealProductBuilderRow>;

export const getColumnDefaultRenderOptions = (
  key: Extract<keyof DealProductBuilderRow, string>,
  testIdPrefix: string
): DealProductBuilderColumn => {
  return {
    field: key,
    type: 'custom',
    params: {
      valueGetter: ({ data }: ValueGetterParams<DealProductBuilderRow>) => {
        if (!data) {
          return '';
        }
        return get(data, key);
      },
      cellRenderer: ({ data, valueFormatted, context }: ICellRendererParams) => {
        if (!data) {
          return '';
        }
        return (
          <Text
            className="ag-cell-value-container"
            data-testid={`${testIdPrefix}-${key}`}
            theme={context?.current.theme}
          >
            {valueFormatted}
          </Text>
        );
      },
    },
  };
};

const TEST_ID_PREFIX = 'deal-product-builder';

export type DealProductBuilderFieldDefinitions = Record<
  'leg' | 'productTemplate' | 'suffix' | 'productDetails',
  FieldDefinition
>;

export function useDealProductBuilderColDef(
  productDetailsDisclosure: ReturnType<
    typeof useDisclosureWithContext<{ rowIndex: number; rowData: DealProductBuilderRow }>
  >,
  customColumnDefs: DealProductBuilderColumn[] = [],
  fieldDefinitions: DealProductBuilderFieldDefinitions
): DealProductBuilderColumn[] {
  const dealCommonCols = useMemo(() => dealCommonColumns(fieldDefinitions), [fieldDefinitions]);
  const dealFieldsDialogButtonCol = useMemo(
    () => dealFieldsDialogButton(productDetailsDisclosure, fieldDefinitions),
    [productDetailsDisclosure, fieldDefinitions]
  );

  return useMemo(
    () => [...dealCommonCols, ...customColumnDefs, dealFieldsDialogButtonCol],
    [dealCommonCols, customColumnDefs, dealFieldsDialogButtonCol]
  );
}

function dealCommonColumns(
  fieldDefinitions: Pick<DealProductBuilderFieldDefinitions, 'leg' | 'productTemplate' | 'suffix'>
): DealProductBuilderColumn[] {
  return [
    {
      field: 'leg',
      type: 'custom',
      title: getColumnTitle(fieldDefinitions.leg),
      description: getColumnTooltip(fieldDefinitions.leg),
      editable: false,
      minWidth: 60,
      width: 60,
      maxWidth: 60,
      cellClass: 'ag-cell-leg-cell',
      params: {
        valueGetter: ({ data }) => {
          if (!data) {
            return null;
          }
          return {
            legNumber: data.leg,
            isValid: data.isLegValid,
          };
        },
        cellRenderer: ({
          data,
          value,
          context,
        }: ICellRendererParams<
          DealProductBuilderRow,
          {
            legNumber: number;
            isValid: boolean;
          }
        >) => {
          if (!data || !value) {
            return null;
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Text
                className="ag-cell-value-container"
                data-testid="deal-product-builder-leg-number"
                theme={context?.current.theme}
              >
                {value.legNumber}
              </Text>
              <Icon
                size={16}
                color={value.isValid ? 'colors.green.lighten' : 'colors.gray.090'}
                icon={value.isValid ? IconName.CheckCircleSolid : IconName.MinusCircle}
                data-testid="deal-product-builder-leg-validation-icon"
              />
            </div>
          );
        },
      },
    },
    {
      ...getColumnDefaultRenderOptions('productTemplate', TEST_ID_PREFIX),
      title: getColumnTitle(fieldDefinitions.productTemplate),
      description: getColumnTooltip(fieldDefinitions.productTemplate),
      editable: false,
      minWidth: 200,
      width: 200,
      cellClass: 'ag-cell-editable',
      params: {
        ...getColumnDefaultRenderOptions('productTemplate', TEST_ID_PREFIX).params,
        cellRenderer: (params: ICellRendererParams) => {
          const value = params.value;
          if (!value || value.trim() === '') {
            return <Text color="colorTextMuted">Complete leg details</Text>;
          }
          return <Text color="colorTextDefault">{value}</Text>;
        },
      },
    },
    {
      field: 'uniqueSuffix',
      type: 'textLiveEdit',
      title: getColumnTitle(fieldDefinitions.suffix),
      description: getColumnTooltip(fieldDefinitions.suffix),
      editable: true,
      minWidth: 100,
      width: 100,
      cellClass: 'ag-cell-editable',
      params: {
        source: 'suffixInput',
        placeholder: 'Optional',
        ariaLabel: 'Unique suffix',
      },
    },
  ];
}

function dealFieldsDialogButton(
  dealFieldsButtonDisclosure: ReturnType<
    typeof useDisclosureWithContext<{ rowIndex: number; rowData: DealProductBuilderRow }>
  >,
  fieldDefinitions: Pick<DealProductBuilderFieldDefinitions, 'productDetails'>
): DealProductBuilderColumn {
  const buttonName = getColumnTitle(fieldDefinitions.productDetails);
  return {
    ...getColumnDefaultRenderOptions('otcFields', TEST_ID_PREFIX),
    title: buttonName,
    description: getColumnTooltip(fieldDefinitions.productDetails),
    editable: false,
    minWidth: 120,
    width: 120,
    cellClass: 'ag-cell-otc-fields-cell',
    params: {
      ...getColumnDefaultRenderOptions('otcFields', TEST_ID_PREFIX).params,
      valueGetter: ({
        data,
      }: ValueGetterParams<DealProductBuilderRow>): {
        isValid: boolean;
        isEnabled: boolean;
      } | null => {
        if (!data) {
          return null;
        }
        return {
          isValid: data.isLegValid,
          isEnabled: data.baseAsset != null,
        };
      },
      cellRenderer: ({
        data,
        node,
        value,
      }: ICellRendererParams<
        DealProductBuilderRow,
        {
          isValid: boolean;
          isEnabled: boolean;
        }
      >) => {
        if (!data) {
          return null;
        }

        const button = (
          <Button
            startIcon={IconName.DocumentText}
            variant={value?.isValid ? ButtonVariants.Positive : ButtonVariants.Default}
            size={FormControlSizes.Small}
            disabled={!value?.isEnabled}
            onClick={() => {
              if (
                dealFieldsButtonDisclosure &&
                node.rowIndex !== null &&
                node.rowIndex !== undefined &&
                value?.isEnabled
              ) {
                dealFieldsButtonDisclosure.open({ rowIndex: node.rowIndex, rowData: data });
              }
            }}
          >
            {buttonName}
          </Button>
        );

        return value?.isEnabled ? button : <Tooltip tooltip="Set Underlying to enable OTC Fields">{button}</Tooltip>;
      },
    },
  };
}
